import { useEffect, useState } from 'react';
import { NJButton, NJIconButton, NJText } from '@engie-group/fluid-design-system-react';
import { ROLE_LABEL, USERS } from './data';
import type { SharingActions } from './hooks';
import { directory, findPrincipal } from './permissions';
import { AccessRow, CreateTeamOption, PrincipalAvatar, GeneralAccess, PrincipalSearch, ReachNotice, RoleMenu, run, SelectedChips, TeamCreator, TransferOwner } from './parts';
import type { AccessEntry, GrantRole, NewAoTeam, Principal, SharingPermissions, SharingState } from './types';

export type SurfaceStep = 'sharing' | 'team' | 'transfer';

export interface SurfaceProps {
    state: SharingState;
    entries: AccessEntry[];
    perms: SharingPermissions;
    viewerUserId: string;
    actions: SharingActions;
    step: SurfaceStep;
    setStep: (step: SurfaceStep) => void;
    onClose: () => void;
}

function useAccessHandlers(actions: SharingActions) {
    return {
        changeRole: (p: Principal, role: GrantRole) => run(() => actions.changeRole.mutateAsync({ principalId: p.id, role }), `${p.name} : ${ROLE_LABEL[role].toLowerCase()}`),
        remove: (p: Principal) => run(() => actions.revoke.mutateAsync(p.id), `Accès retiré à ${p.name}`),
        share: (ps: Principal[], role: GrantRole) => run(
            () => actions.share.mutateAsync({ principalIds: ps.map((p) => p.id), role }),
            `${ps.map((p) => p.name).join(', ')} : ${ROLE_LABEL[role].toLowerCase()}`,
        ),
        transfer: (user: Principal) => run(() => actions.transfer.mutateAsync(user.id), `${user.name} est maintenant propriétaire. Vous gardez « Peut modifier ».`),
        createTeam: (team: NewAoTeam) => run(() => actions.createTeam.mutateAsync(team), `Équipe « ${team.name} » créée et partagée (${ROLE_LABEL[team.role].toLowerCase()})`),
    };
}

/** Everyone the network can still be shared with. */
function candidates(state: SharingState, exclude: string[] = []) {
    const taken = new Set([state.ownerId, ...state.grants.map((g) => g.principalId), ...exclude]);
    return directory(state).filter((p) => !taken.has(p.id));
}

function SurfaceHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
    return (
        <div className="ns-surface__head">
            <div className="ns-row__text">
                <NJText scale="lg" className="ns-strong">{title}</NJText>
                {subtitle && <NJText scale="xs" variant="secondary" className="ns-wrap">{subtitle}</NJText>}
            </div>
            <NJIconButton icon="close" label="Fermer" variant="tertiary" size="sm" onClick={onClose} />
        </div>
    );
}

function useEscape(onClose: () => void) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !e.defaultPrevented && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
}

function AccessList({ entries, perms, viewerUserId, handlers, onTransfer }: Pick<SurfaceProps, 'entries' | 'perms' | 'viewerUserId'> & { handlers: ReturnType<typeof useAccessHandlers>; onTransfer: (p: Principal) => void }) {
    return (
        <ul className="ns-list" aria-label="Personnes et groupes avec accès">
            {entries.map((entry) => (
                <AccessRow
                    key={entry.principal.id}
                    entry={entry}
                    canManage={perms.canManage}
                    isMe={entry.principal.id === viewerUserId}
                    onChange={(role) => handlers.changeRole(entry.principal, role)}
                    onRemove={() => handlers.remove(entry.principal)}
                    onTransfer={perms.canTransfer && entry.principal.kind === 'user' ? () => onTransfer(entry.principal) : undefined}
                />
            ))}
        </ul>
    );
}

/** Requests sent from the « Accès non autorisé » page; the owner picks the right before accepting. */
function RequestRow({ user, requestedAt, actions }: { user: Principal; requestedAt: string; actions: SharingActions }) {
    const [role, setRole] = useState<GrantRole>('read');
    const busy = actions.acceptRequest.isPending || actions.declineRequest.isPending;
    const date = new Date(requestedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return (
        <li className="ns-row ns-request">
            <PrincipalAvatar principal={user} />
            <div className="ns-row__text">
                <NJText scale="sm" className="ns-strong">{user.name}</NJText>
                <NJText scale="xs" variant="secondary">Demande l’accès · {date}</NJText>
            </div>
            <RoleMenu role={role} label={`Droit à accorder à ${user.name}`} onChange={setRole} />
            <NJButton label="Refuser" variant="secondary" emphasis="minimal" scale="sm" disabled={busy}
                onClick={() => void run(() => actions.declineRequest.mutateAsync(user.id), `Demande de ${user.name} refusée`)} />
            <NJButton label="Accepter" scale="sm" disabled={busy}
                onClick={() => void run(() => actions.acceptRequest.mutateAsync({ userId: user.id, role }), `${user.name} : ${ROLE_LABEL[role].toLowerCase()}`)} />
        </li>
    );
}

function RequestList({ state, actions }: Pick<SurfaceProps, 'state' | 'actions'>) {
    const rows = state.requests.flatMap((r) => {
        const user = findPrincipal(state, r.userId);
        return user ? [{ user, requestedAt: r.requestedAt }] : [];
    });
    if (!rows.length) return null;
    return (
        <section className="ns-block">
            <NJText scale="xs" variant="secondary" className="ns-overline">Demandes d’accès ({rows.length})</NJText>
            <ul className="ns-list" aria-label="Demandes d’accès en attente">
                {rows.map((r) => <RequestRow key={r.user.id} {...r} actions={actions} />)}
            </ul>
        </section>
    );
}

function useTransfer(setStep: (step: SurfaceStep) => void) {
    const [target, setTarget] = useState<Principal | null>(null);
    return {
        target,
        open: (p: Principal | null) => { setTarget(p); setStep('transfer'); },
    };
}

function TransferView({ state, actions, setStep, target, handlers }: Pick<SurfaceProps, 'state' | 'actions' | 'setStep'> & { target: Principal | null; handlers: ReturnType<typeof useAccessHandlers> }) {
    return (
        <TransferOwner
            ownerName={findPrincipal(state, state.ownerId)?.name ?? ''}
            initial={target}
            options={USERS.filter((u) => u.id !== state.ownerId)}
            onBack={() => setStep('sharing')}
            onConfirm={handlers.transfer}
            pending={actions.transfer.isPending}
        />
    );
}

/** Variant B — quick « Qui a accès » popover anchored under the avatar stack. */
export function AccessPopover({ onManage, ...props }: SurfaceProps & { onManage: () => void }) {
    const { state, entries, perms, actions, step, setStep, onClose } = props;
    const handlers = useAccessHandlers(actions);
    const transfer = useTransfer(setStep);
    useEscape(onClose);
    const shared = entries.length > 1;
    let subtitle = shared ? `${entries.length} accès · le droit le plus élevé s’applique` : 'Réseau privé';
    if (!perms.canManage) subtitle = `Vous pouvez consulter ce réseau via ${perms.via.join(', ')}. Seuls la propriétaire et les éditeurs gèrent les accès.`;

    return (
        <div className="ns-surface ns-popover" role="dialog" aria-label="Qui a accès">
            {step === 'transfer' ? (
                <TransferView {...props} target={transfer.target} handlers={handlers} />
            ) : step === 'team' ? (
                <TeamCreator onBack={() => setStep('sharing')} onCreate={handlers.createTeam} pending={actions.createTeam.isPending} />
            ) : (
                <>
                    <SurfaceHeader title="Qui a accès" subtitle={subtitle} onClose={onClose} />
                    {perms.canManage && (
                        <PrincipalSearch
                            options={candidates(state)}
                            placeholder="Ajouter une personne ou un groupe…"
                            onPick={(p) => handlers.share([p], 'read')}
                            footer={<CreateTeamOption onClick={() => setStep('team')} />}
                        />
                    )}
                    <AccessList {...props} handlers={handlers} onTransfer={transfer.open} />
                    {!shared && <GeneralAccess shared={false} />}
                    {perms.canManage && (
                        <div className="ns-footer ns-footer--split">
                            <NJButton label="Équipe AO" variant="secondary" emphasis="minimal" scale="sm" icon="group_add" onClick={() => setStep('team')} />
                            <NJButton label="Gérer l’accès" variant="secondary" scale="sm" icon="open_in_full" onClick={onManage} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/** Full share dialog opened by « Partager » or « Gérer l'accès ». */
export function ShareModal({ networkName, ...props }: SurfaceProps & { networkName: string }) {
    const { state, entries, actions, step, setStep, onClose } = props;
    const handlers = useAccessHandlers(actions);
    useEscape(onClose);
    const [selected, setSelected] = useState<Principal[]>([]);
    const [role, setRole] = useState<GrantRole>('read');
    const transfer = useTransfer(setStep);

    const invite = async () => {
        if (await handlers.share(selected, role)) setSelected([]);
    };

    return (
        <div className="ns-scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ns-surface ns-modal" role="dialog" aria-modal="true" aria-label="Partager le réseau">
                {step === 'transfer' ? (
                    <TransferView {...props} target={transfer.target} handlers={handlers} />
                ) : step === 'team' ? (
                    <TeamCreator onBack={() => setStep('sharing')} onCreate={handlers.createTeam} pending={actions.createTeam.isPending} />
                ) : (
                    <>
                        <SurfaceHeader title={`Partager « ${networkName} »`} subtitle="Réseau · Contrat Les Tilleuls" onClose={onClose} />
                        <div className="ns-invite">
                            <PrincipalSearch
                                options={candidates(state, selected.map((p) => p.id))}
                                placeholder={selected.length ? 'Ajouter…' : 'Nom, équipe AO, équipe, filière, territoire, GBU…'}
                                autoFocus
                                chips={<SelectedChips principals={selected} onRemove={(id) => setSelected((s) => s.filter((p) => p.id !== id))} />}
                                onPick={(p) => setSelected((s) => [...s, p])}
                                footer={<CreateTeamOption onClick={() => setStep('team')} />}
                            />
                            <RoleMenu role={role} label="Droit à attribuer" outlined onChange={setRole} />
                            <NJButton label="Inviter" disabled={!selected.length || actions.share.isPending} onClick={invite} />
                        </div>
                        <ReachNotice principals={selected} />
                        <RequestList state={state} actions={actions} />
                        <section className="ns-block">
                            <NJText scale="xs" variant="secondary" className="ns-overline">Personnes et groupes avec accès</NJText>
                            <AccessList {...props} handlers={handlers} onTransfer={transfer.open} />
                        </section>
                        <section className="ns-block">
                            <NJText scale="xs" variant="secondary" className="ns-overline">Accès général</NJText>
                            <GeneralAccess shared={entries.length > 1} />
                        </section>
                        <div className="ns-footer ns-footer--split">
                            <div className="ns-footer__group">
                                <NJButton label="Créer une équipe AO" variant="secondary" emphasis="minimal" icon="group_add" onClick={() => setStep('team')} />
                                {props.perms.canTransfer && <NJButton label="Transférer la propriété" variant="secondary" emphasis="minimal" icon="swap_horiz" onClick={() => transfer.open(null)} />}
                            </div>
                            <NJButton label="Fermer" onClick={onClose} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
